import { useEffect, useState } from 'react';
import { fmtVND } from '../utils/format';
import { listCumulativeRevenue } from '../api/cumulativeRevenue';
import CustomerRevenueChartModal from '../components/CustomerRevenueChartModal';
import CskhLogTab from './CskhLogTab';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { useModal } from '../components/ModalProvider';
import { listGroups } from '../api/groups';
import { createTask } from '../api/tasks';
import CreateTaskFromCustomers from '../components/CreateTaskFromCustomers';

const MONTHS = Array.from({ length: 12 }, (_, i) => `T${i + 1}`);

// Lấy mảng 12 tháng theo chỉ số (MH / VC / Tổng).
function monthsOf(row, metric) {
  const mh = row.mh || [];
  const vc = row.vc || [];
  if (metric === 'mh') return MONTHS.map((_, i) => Number(mh[i]) || 0);
  if (metric === 'vc') return MONTHS.map((_, i) => Number(vc[i]) || 0);
  return MONTHS.map((_, i) => (Number(mh[i]) || 0) + (Number(vc[i]) || 0));
}

// Số tháng sử dụng TRONG NĂM: từ tháng đầu tiên có doanh thu (tổng mh+vc) → tháng hiện tại
// (nếu là năm nay) hoặc hết T12 (năm cũ). Tháng = 0 xen giữa vẫn tính. Mỗi năm đếm lại.
function monthsUsedInYear(row, year) {
  const mh = row.mh || [];
  const vc = row.vc || [];
  let first = -1;
  for (let i = 0; i < 12; i++) {
    if ((Number(mh[i]) || 0) + (Number(vc[i]) || 0) > 0) { first = i; break; }
  }
  if (first === -1) return 0;
  const now = vnNow();
  const endIdx = Number(year) < now.getFullYear() ? 11 : Number(year) === now.getFullYear() ? now.getMonth() - 1 : -1;
  return endIdx < first ? 0 : endIdx - first + 1;
}

// Chỉ số tháng ĐÃ CHỐT cuối cùng của năm (năm nay: tháng liền trước; năm cũ: T12; tương lai: -1).
function closedEndIdx(year) {
  const now = vnNow();
  return Number(year) < now.getFullYear() ? 11 : Number(year) === now.getFullYear() ? now.getMonth() - 1 : -1;
}

// Doanh thu trung bình (mỗi tháng sử dụng) trong năm: tổng doanh thu các tháng ĐÃ CHỐT
// (không tính tháng hiện tại chưa chốt) ÷ số tháng sử dụng.
function avgRevenueInYear(row, year, metric) {
  const used = monthsUsedInYear(row, year);
  if (!used) return 0;
  const end = closedEndIdx(year);
  if (end < 0) return 0;
  const closedTotal = monthsOf(row, metric).slice(0, end + 1).reduce((a, b) => a + b, 0);
  return Math.round(closedTotal / used);
}

// Tăng/giảm doanh thu giữa 2 tháng ĐÃ CHỐT gần nhất = tháng gần nhất − tháng liền trước.
// Cần ≥ 2 tháng đã chốt; nếu không trả null (hiển thị "—").
function revChangeInYear(row, year, metric) {
  const end = closedEndIdx(year);
  if (end < 1) return null;
  const m = monthsOf(row, metric);
  return m[end] - m[end - 1];
}

// Xếp hạng KH theo doanh thu 1 THÁNG (cột "Xếp hạng" năm 2026).
// Xét MH và VC RIÊNG BIỆT, đạt bất kỳ ngưỡng nào (OR) là lên hạng; kiểm từ cao xuống thấp.
const RANK_TIERS = [
  { label: 'VIP', mh: 300e6, vc: 100e6, color: 'var(--vip)' },
  { label: 'Cấp 1', mh: 200e6, vc: 80e6, color: 'var(--accent)' },
  { label: 'Cấp 2', mh: 100e6, vc: 50e6, color: 'var(--info)' },
  { label: 'Cấp 3', mh: 70e6, vc: 30e6, color: 'var(--warn)' },
];
const RANK_DEFAULT = { label: 'Phổ thông', color: 'var(--muted)' };
function rankByMonth(mhVal, vcVal) {
  const MH = Number(mhVal) || 0;
  const VC = Number(vcVal) || 0;
  return RANK_TIERS.find((t) => MH >= t.mh || VC >= t.vc) || RANK_DEFAULT;
}
// Xếp hạng NĂM: cùng ngưỡng như xếp hạng tháng nhưng xét trên DOANH THU TRUNG BÌNH năm
// (MH TB và VC TB riêng biệt). Trả null nếu KH chưa phát sinh tháng nào trong năm.
function yearRankOf(row, year) {
  if (!monthsUsedInYear(row, year)) return null;
  return rankByMonth(avgRevenueInYear(row, year, 'mh'), avgRevenueInYear(row, year, 'vc'));
}
// Tháng vừa chốt xong (GMT+7) = tháng liền trước hôm nay → dùng để xếp hạng.
function vnNow() {
  return new Date(Date.now() + (7 * 60 + new Date().getTimezoneOffset()) * 60000);
}

function CumulativeRevenueTab() {
  const { me } = useAuth();
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  // GĐ/CV/TP và Phòng CSKH được tích chọn KH + tạo công việc trên màn Doanh thu lũy kế.
  const canCreateTask = me && (me.role === 'GD' || me.role === 'CV' || me.role === 'TP' || (me.departments || []).includes('Phòng CSKH'));
  const [picked, setPicked] = useState(new Map()); // custCode -> custName
  const [data, setData] = useState({ items: [], total: 0, totalPages: 1, years: [] });
  const [year, setYear] = useState(null);
  const [metric, setMetric] = useState('total'); // 'mh' | 'vc' | 'total'
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [sortBy, setSortBy] = useState(null); // null | 'total' | '0'..'11'
  const [sortDir, setSortDir] = useState('desc');
  const [sale, setSale] = useState(''); // lọc theo 1 sale ('' = tất cả)
  const [cmpA, setCmpA] = useState(''); // so sánh: tháng A ('' = tắt)
  const [cmpB, setCmpB] = useState(''); // so sánh: tháng B
  const [loading, setLoading] = useState(true);
  const [chart, setChart] = useState(null); // { code, name } khi mở biểu đồ
  const [exporting, setExporting] = useState(false); // đang tạo file Excel
  // GĐ/CV (xem được TOÀN BỘ KH mọi năm) mới có nút tải Excel lũy kế.
  const canExport = me && (me.role === 'GD' || me.role === 'CV');

  // Năm 2026 có thêm cột "Xếp hạng" ở cuối (ẩn cột HẠNG cũ). Các năm 2022–2025 giữ nguyên.
  const is2026 = Number(year) === 2026;
  // Xếp hạng theo THÁNG VỪA CHỐT (tháng liền trước hôm nay). VD 6/7 → xếp hạng tháng 6 (index 5).
  // Nếu đang xem năm quá khứ đã trọn thì lấy tháng 12.
  const rankMonthIdx = (() => {
    if (!is2026) return null;
    const now = vnNow();
    if (now.getFullYear() > 2026) return 11;
    if (now.getFullYear() === 2026) return now.getMonth() - 1 >= 0 ? now.getMonth() - 1 : null;
    return null; // năm 2026 còn ở tương lai → chưa có tháng nào chốt
  })();
  // So sánh 2 tháng: khi chọn đủ 2 tháng khác nhau (chỉ áp dụng năm 2026).
  const compareMode = is2026 && cmpA !== '' && cmpB !== '' && cmpA !== cmpB;
  const cmpAi = Number(cmpA);
  const cmpBi = Number(cmpB);
  // Tháng dùng để xếp hạng: khi so sánh thì lấy tháng B (tháng sau); ngược lại là tháng vừa chốt.
  const rankMonthEff = compareMode ? cmpBi : rankMonthIdx;
  // Cột "Tăng/giảm" = tháng ĐÍCH − tháng GỐC. So sánh: B − A; thường: tháng vừa chốt − tháng liền trước.
  const chgToIdx = compareMode ? cmpBi : rankMonthIdx;
  const chgFromIdx = compareMode ? cmpAi : (rankMonthIdx != null ? rankMonthIdx - 1 : null);
  // Có xếp hạng tháng → mặc định sắp theo hạng (VIP→Phổ thông) khi chưa chọn cột nào.
  const canRank = is2026 && rankMonthEff != null && rankMonthEff >= 0;
  const effSortBy = sortBy ?? (canRank ? 'rank' : undefined);

  useEffect(() => {
    setLoading(true);
    listCumulativeRevenue({
      year: year || undefined, search, sale: sale || undefined, page, limit: pageSize, metric,
      sortBy: effSortBy, sortDir, rankMonth: canRank ? rankMonthEff : undefined,
      // 2 tháng của cột Tăng/giảm (để sắp xếp đúng khi so sánh).
      chgTo: chgToIdx != null && chgToIdx >= 0 ? chgToIdx : undefined,
      chgFrom: chgFromIdx != null && chgFromIdx >= 0 ? chgFromIdx : undefined,
    })
      .then((d) => {
        setData(d);
        if (!year) setYear(d.year);
      })
      .finally(() => setLoading(false));
  }, [year, search, sale, page, pageSize, metric, effSortBy, sortDir, canRank, rankMonthEff, chgToIdx, chgFromIdx]);

  useEffect(() => setPage(1), [year, search, sale, pageSize, metric, effSortBy, sortDir]);

  // Bấm header cột doanh thu: giảm -> tăng -> mặc định.
  const toggleSort = (key) => {
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir('desc');
    } else if (sortDir === 'desc') {
      setSortDir('asc');
    } else {
      setSortBy(null);
      setSortDir('desc');
    }
  };
  const arrow = (key) => (sortBy === key ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ' ⇅');

  const togglePick = (code, name, on) =>
    setPicked((prev) => {
      const m = new Map(prev);
      if (on) m.set(code, name);
      else m.delete(code);
      return m;
    });
  const allOnPage = data.items.length > 0 && data.items.every((r) => picked.has(r.custCode));
  const togglePickAll = (on) =>
    setPicked((prev) => {
      const m = new Map(prev);
      data.items.forEach((r) => (on ? m.set(r.custCode, r.custName) : m.delete(r.custCode)));
      return m;
    });

  async function openCreateTask() {
    if (!picked.size) return toast('Hãy tích chọn KH trước', 'warn');
    const groups = await listGroups();
    const pickedArr = [...picked.entries()].map(([code, name]) => ({ code, name }));
    openModal(
      <CreateTaskFromCustomers
        picked={pickedArr}
        groups={groups}
        hideGroup={me.role === 'TP'}
        onCancel={closeModal}
        onSubmit={async (form) => {
          try {
            const res = await createTask({ ...form, custCodes: pickedArr.map((c) => c.code) });
            setPicked(new Map());
            closeModal();
            toast(res?.message || 'Đã tạo & giao công việc');
          } catch (err) {
            toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
          }
        }}
      />
    );
  }

  const metricLabel = { mh: 'Mua hộ', vc: 'Vận chuyển', total: 'Tổng (Mua hộ + VC)' };

  // Lấy TOÀN BỘ KH của 1 năm (BE cap limit=500 nên phải phân trang gom hết).
  async function fetchAllYear(y) {
    const out = [];
    let p = 1;
    let totalPages = 1;
    do {
      const d = await listCumulativeRevenue({ year: y, page: p, limit: 500, metric });
      (d.items || []).forEach((r) => out.push(r));
      totalPages = d.totalPages || 1;
      p += 1;
    } while (p <= totalPages);
    return out;
  }

  // Tải Excel doanh thu lũy kế — form giống bảng web (Mã KH·Tên KH·Sale·Tuyến·T1..T12·Cả năm),
  // theo metric đang chọn. scope='year': năm đang xem (1 sheet). scope='all': mỗi năm 1 sheet.
  async function exportExcel(scope) {
    if (exporting) return;
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      const { saveFile } = await import('../download');
      const years = scope === 'all' ? [...(data.years || [])].sort((a, b) => a - b) : [Number(year)];
      if (!years.length) { toast('Chưa có dữ liệu để tải', 'warn'); return; }
      const wb = XLSX.utils.book_new();
      for (const y of years) {
        const items = await fetchAllYear(y);
        const aoa = [['STT', 'Mã KH', 'Tên KH', 'Sale', 'Tuyến', ...MONTHS, 'Cả năm']];
        items.forEach((r, i) => {
          const mo = monthsOf(r, metric);
          aoa.push([i + 1, r.custCode || '', r.custName || '', r.saleName || r.sale || '', r.route || '', ...mo, mo.reduce((a, b) => a + b, 0)]);
        });
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let R = 1; R <= range.e.r; R++) {
          for (let C = 5; C <= 17; C++) { // cột F..R: T1..T12 + Cả năm -> định dạng #,##0
            const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
            if (cell && typeof cell.v === 'number') cell.z = '#,##0';
          }
        }
        ws['!cols'] = [{ wch: 5 }, { wch: 12 }, { wch: 24 }, { wch: 18 }, { wch: 8 }, ...MONTHS.map(() => ({ wch: 13 })), { wch: 15 }];
        XLSX.utils.book_append_sheet(wb, ws, String(y));
      }
      const metricFile = { mh: 'MuaHo', vc: 'VanChuyen', total: 'Tong' }[metric] || 'Tong';
      const fname = scope === 'all' ? `DoanhThuLuyKe_TatCaNam_${metricFile}.xlsx` : `DoanhThuLuyKe_${years[0]}_${metricFile}.xlsx`;
      const outArr = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      await saveFile({ blob: new Blob([outArr], { type: 'application/octet-stream' }), filename: fname });
      toast('Đã tạo file Excel');
    } catch (e) {
      toast('Tạo Excel lỗi: ' + (e.message || ''), 'danger');
    } finally {
      setExporting(false);
    }
  }

  // colSpan cho dòng trống/đang tải. Năm 2026: HẠNG cũ đổi thành "Xếp hạng" (±0) + thêm "Số tháng SD", "DT TB/tháng", "Xếp hạng năm" (+3).
  // Chế độ so sánh: Mã KH·Tên KH·Sale·Tuyến·Tháng A·Tháng B·Tăng/giảm·Xếp hạng tháng·Xếp hạng năm = 9 (+checkbox).
  const colCount = compareMode
    ? 9 + (canCreateTask ? 1 : 0)
    : 20 + (canCreateTask ? 1 : 0) + (is2026 ? 4 : 0);

  if (!loading && (data.years || []).length === 0) {
    return (
      <div className="card">
        <div className="empty" style={{ padding: 40, textAlign: 'center' }}>
          🔒 Bạn không có quyền xem doanh thu lũy kế.
          <div className="small muted" style={{ marginTop: 6 }}>
            Chỉ Giám đốc và Phòng CSKH mới xem được dữ liệu lũy kế các năm ≤ 2025.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="row between" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div className="row" style={{ gap: 12, alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>💰 Doanh thu lũy kế ({data.total} KH)</h3>
          {canCreateTask && (
            <button className="btn sm" disabled={!picked.size} onClick={openCreateTask}>
              ＋ Tạo công việc{picked.size ? ` (${picked.size})` : ''}
            </button>
          )}
          {canExport && (
            <>
              <button className="btn sm ghost" disabled={exporting} onClick={() => exportExcel('year')} title={`Tải Excel doanh thu lũy kế năm ${year || ''} (${metricLabel[metric]})`}>
                {exporting ? '⏳ Đang tạo…' : `📥 Excel năm ${year || ''}`}
              </button>
              <button className="btn sm ghost" disabled={exporting} onClick={() => exportExcel('all')} title={`Tải Excel doanh thu lũy kế TẤT CẢ các năm — mỗi năm 1 sheet (${metricLabel[metric]})`}>
                📥 Tất cả năm
              </button>
            </>
          )}
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <select value={year || ''} onChange={(e) => setYear(Number(e.target.value))} style={{ width: 'auto' }}>
            {(data.years || []).map((y) => (
              <option key={y} value={y}>
                Năm {y}
              </option>
            ))}
          </select>
          <select value={sale} onChange={(e) => setSale(e.target.value)} style={{ width: 'auto', maxWidth: 200 }} title="Lọc theo sale">
            <option value="">Tất cả sale</option>
            {(data.sales || []).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {is2026 && (
            <div className="row" style={{ gap: 4, alignItems: 'center' }} title="So sánh doanh thu 2 tháng">
              <span className="small muted">So sánh</span>
              <select value={cmpA} onChange={(e) => setCmpA(e.target.value)} style={{ width: 'auto' }}>
                <option value="">Tháng…</option>
                {Array.from({ length: closedEndIdx(year) + 1 }, (_, i) => (
                  <option key={i} value={i}>T{i + 1}</option>
                ))}
              </select>
              <span className="muted">⇄</span>
              <select value={cmpB} onChange={(e) => setCmpB(e.target.value)} style={{ width: 'auto' }}>
                <option value="">Tháng…</option>
                {Array.from({ length: closedEndIdx(year) + 1 }, (_, i) => (
                  <option key={i} value={i}>T{i + 1}</option>
                ))}
              </select>
              {(cmpA !== '' || cmpB !== '') && (
                <button className="btn ghost sm" title="Bỏ so sánh" onClick={() => { setCmpA(''); setCmpB(''); }}>✕</button>
              )}
            </div>
          )}
          <div className="seg">
            {['mh', 'vc', 'total'].map((m) => (
              <button key={m} className={metric === m ? 'active' : ''} onClick={() => setMetric(m)}>
                {metricLabel[m]}
              </button>
            ))}
          </div>
          <input
            style={{ width: 200 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍 Tìm mã KH / tên / sale…"
          />
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{ width: 'auto' }}>
            {[100, 200, 500].map((n) => (
              <option key={n} value={n}>
                {n}/trang
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="tbl-wrap">
        <table className="cumrev-tbl" style={{ fontSize: 12.5, minWidth: 1100 }}>
          <thead>
            <tr>
              {canCreateTask && (
                <th className="c">
                  <input type="checkbox" checked={allOnPage} onChange={(e) => togglePickAll(e.target.checked)} title="Chọn tất cả trong trang" />
                </th>
              )}
              <th>Mã KH</th>
              <th>Tên KH</th>
              <th>Sale</th>
              <th className="c">Tuyến</th>
              {!is2026 && (
                <th
                  className={'c sortable' + (sortBy === 'level' ? ' sorted' : '')}
                  onClick={() => toggleSort('level')}
                  title="Bấm để sắp xếp theo hạng"
                >
                  Hạng
                  <span className="sort-ico">{arrow('level')}</span>
                </th>
              )}
              {is2026 && !compareMode && (
                <th
                  className={'c sortable' + (effSortBy === 'rank' ? ' sorted' : '')}
                  onClick={() => {
                    if (!canRank) return;
                    // Mặc định đã sắp theo hạng (desc) → bấm để đảo desc↔asc, không về trạng thái "không sắp".
                    setSortDir((d) => (effSortBy === 'rank' && d === 'desc' ? 'asc' : 'desc'));
                    setSortBy('rank');
                  }}
                  title="Bấm để sắp xếp theo xếp hạng"
                >
                  Xếp hạng tháng
                  <span className="sort-ico">{effSortBy === 'rank' ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ' ⇅'}</span>
                </th>
              )}
              {compareMode && (
                <>
                  <th className="r">T{cmpAi + 1}</th>
                  <th className="r">T{cmpBi + 1}</th>
                  <th
                    className={'r sortable' + (sortBy === 'revchange' ? ' sorted' : '')}
                    onClick={() => toggleSort('revchange')}
                    title={`Tăng/giảm = T${cmpBi + 1} − T${cmpAi + 1}. Bấm để sắp xếp.`}
                  >
                    Tăng/giảm
                    <span className="sort-ico">{arrow('revchange')}</span>
                  </th>
                  <th
                    className={'c sortable' + (effSortBy === 'rank' ? ' sorted' : '')}
                    onClick={() => { if (!canRank) return; setSortDir((d) => (effSortBy === 'rank' && d === 'desc' ? 'asc' : 'desc')); setSortBy('rank'); }}
                    title={`Xếp hạng theo doanh thu T${cmpBi + 1}. Bấm để sắp xếp.`}
                  >
                    Xếp hạng tháng
                    <span className="sort-ico">{effSortBy === 'rank' ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ' ⇅'}</span>
                  </th>
                </>
              )}
              {!compareMode && MONTHS.map((m, i) => (
                <th
                  key={m}
                  className={'r sortable' + (sortBy === String(i) ? ' sorted' : '')}
                  onClick={() => toggleSort(String(i))}
                  title="Bấm để sắp xếp theo cột này"
                >
                  {m}
                  <span className="sort-ico">{arrow(String(i))}</span>
                </th>
              ))}
              {!compareMode && (
                <th
                  className={'r sortable' + (sortBy === 'total' ? ' sorted' : '')}
                  onClick={() => toggleSort('total')}
                  title="Bấm để sắp xếp theo cả năm"
                >
                  Cả năm
                  <span className="sort-ico">{arrow('total')}</span>
                </th>
              )}
              {is2026 && !compareMode && (
                <th
                  className={'r sortable' + (sortBy === 'revchange' ? ' sorted' : '')}
                  onClick={() => toggleSort('revchange')}
                  title="Tăng/giảm doanh thu giữa 2 tháng đã chốt gần nhất (tháng gần nhất − tháng liền trước). Bấm để sắp xếp."
                >
                  Tăng/giảm
                  <span className="sort-ico">{arrow('revchange')}</span>
                </th>
              )}
              {is2026 && !compareMode && <th className="c" title="Số tháng sử dụng trong năm: từ tháng đầu phát sinh doanh thu → tháng hiện tại (tháng =0 xen giữa vẫn tính)">Số tháng SD</th>}
              {is2026 && !compareMode && <th className="r" title="Doanh thu trung bình = tổng doanh thu các tháng đã chốt (không tính tháng hiện tại) ÷ số tháng SD">DT TB/tháng</th>}
              {!compareMode && <th>Mã KH</th>}
              {!compareMode && <th>Tên KH</th>}
              {is2026 && (
                <th
                  className={'c sortable' + (sortBy === 'yearrank' ? ' sorted' : '')}
                  onClick={() => toggleSort('yearrank')}
                  title="Xếp hạng cả năm theo doanh thu trung bình/tháng (cùng ngưỡng như xếp hạng tháng). Bấm để sắp xếp."
                >
                  Xếp hạng năm
                  <span className="sort-ico">{arrow('yearrank')}</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading && !data.items.length ? (
              <tr>
                <td colSpan={colCount} className="empty">
                  Đang tải...
                </td>
              </tr>
            ) : data.items.length ? (
              data.items.map((r) => {
                const months = monthsOf(r, metric);
                const yearTotal = months.reduce((a, b) => a + b, 0);
                return (
                  <tr key={r._id} className={picked.has(r.custCode) ? 'row-picked' : ''}>
                    {canCreateTask && (
                      <td className="c">
                        <input
                          type="checkbox"
                          checked={picked.has(r.custCode)}
                          onChange={(e) => togglePick(r.custCode, r.custName, e.target.checked)}
                        />
                      </td>
                    )}
                    <td>
                      <code style={{ color: 'var(--text)' }}>{r.custCode}</code>
                    </td>
                    <td className="cust-link" onClick={() => setChart({ code: r.custCode, name: r.custName })}>
                      {r.custName}
                    </td>
                    <td style={{ color: !r.saleUserId && r.sale ? 'var(--warn)' : undefined }}>
                      {r.saleName || r.sale || <span className="small muted">—</span>}
                    </td>
                    <td className="c">{r.route ? <span className="tag">{r.route}</span> : '—'}</td>
                    {!is2026 && <td className="c">{r.level || '—'}</td>}
                    {is2026 && !compareMode && (
                      <td className="c">
                        {rankMonthIdx != null ? (() => {
                          const rk = rankByMonth((r.mh || [])[rankMonthIdx], (r.vc || [])[rankMonthIdx]);
                          return (
                            <span style={{ fontSize: 11, fontWeight: 600, color: rk.color, background: `color-mix(in srgb, ${rk.color} 12%, transparent)`, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                              {rk.label}
                            </span>
                          );
                        })() : '—'}
                      </td>
                    )}
                    {compareMode && (() => {
                      const va = months[cmpAi] || 0;
                      const vb = months[cmpBi] || 0;
                      const dc = vb - va;
                      const cColor = dc > 0 ? 'var(--ok, #1a7f45)' : dc < 0 ? 'var(--danger)' : 'var(--muted)';
                      const cText = dc > 0 ? '+' + fmtVND(dc) : dc < 0 ? '−' + fmtVND(Math.abs(dc)) : '0';
                      const rk = rankByMonth((r.mh || [])[cmpBi], (r.vc || [])[cmpBi]);
                      return (
                        <>
                          <td className="r">{va ? fmtVND(va) : ''}</td>
                          <td className="r">{vb ? fmtVND(vb) : ''}</td>
                          <td className="r" style={{ color: cColor, fontWeight: 600 }}>{cText}</td>
                          <td className="c"><span style={{ fontSize: 11, fontWeight: 600, color: rk.color, background: `color-mix(in srgb, ${rk.color} 12%, transparent)`, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>{rk.label}</span></td>
                        </>
                      );
                    })()}
                    {!compareMode && months.map((v, i) => (
                      <td key={i} className="r">
                        {v ? fmtVND(v) : ''}
                      </td>
                    ))}
                    {!compareMode && (
                      <td className="r">
                        <b style={{ color: 'var(--accent2)' }}>{fmtVND(yearTotal)}</b>
                      </td>
                    )}
                    {is2026 && !compareMode && (() => {
                      const d = revChangeInYear(r, year, metric);
                      if (d == null) return <td className="r">—</td>;
                      const color = d > 0 ? 'var(--ok, #1a7f45)' : d < 0 ? 'var(--danger)' : 'var(--muted)';
                      const text = d > 0 ? '+' + fmtVND(d) : d < 0 ? '−' + fmtVND(Math.abs(d)) : '0';
                      return <td className="r" style={{ color, fontWeight: 600 }}>{text}</td>;
                    })()}
                    {is2026 && !compareMode && <td className="c">{monthsUsedInYear(r, year) || '—'}</td>}
                    {is2026 && !compareMode && <td className="r">{avgRevenueInYear(r, year, metric) ? fmtVND(avgRevenueInYear(r, year, metric)) : '—'}</td>}
                    {!compareMode && (
                    <td>
                      <code style={{ color: 'var(--text)' }}>{r.custCode}</code>
                    </td>)}
                    {!compareMode && (
                    <td className="cust-link" onClick={() => setChart({ code: r.custCode, name: r.custName })}>
                      {r.custName}
                    </td>)}
                    {is2026 && (
                      <td className="c">
                        {(() => {
                          const rk = yearRankOf(r, year);
                          return rk ? (
                            <span style={{ fontSize: 11, fontWeight: 600, color: rk.color, background: `color-mix(in srgb, ${rk.color} 12%, transparent)`, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                              {rk.label}
                            </span>
                          ) : '—';
                        })()}
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={colCount} className="empty">
                  Chưa có dữ liệu
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data.totalPages > 1 && (
        <div className="row between" style={{ marginTop: 12 }}>
          <span className="small muted">
            Trang {data.page}/{data.totalPages} · {data.total} KH · {metricLabel[metric]}
          </span>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn ghost sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              ‹ Trước
            </button>
            <button className="btn ghost sm" disabled={page >= data.totalPages} onClick={() => setPage((p) => p + 1)}>
              Sau ›
            </button>
          </div>
        </div>
      )}

      <CustomerRevenueChartModal
        open={!!chart}
        code={chart?.code}
        name={chart?.name}
        onClose={() => setChart(null)}
      />
    </div>
  );
}

export default function CskhPage() {
  const [tab, setTab] = useState('log');
  return (
    <>
      <div className="page-head">
        <div>
          <h2>CSKH</h2>
          <div className="sub">
            {tab === 'log' ? 'Chăm sóc khách hàng theo ngày' : 'Doanh thu lũy kế theo khách hàng'}
          </div>
        </div>
        <div className="seg" style={{ margin: 0 }}>
          <button className={tab === 'log' ? 'active' : ''} onClick={() => setTab('log')}>
            Chăm sóc KH
          </button>
          <button className={tab === 'cumrev' ? 'active' : ''} onClick={() => setTab('cumrev')}>
            Doanh thu lũy kế
          </button>
        </div>
      </div>

      {tab === 'cumrev' && <CumulativeRevenueTab />}
      {tab === 'log' && <CskhLogTab />}
    </>
  );
}
